import { motion } from "framer-motion";
import { Heart, Star } from "lucide-react";

const LARONDA_IMG = "https://customer-assets.emergentagent.com/job_pet-care-access/artifacts/5lsdhq2r_IMG_1664.jpeg";

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-32 bg-[#FDFBF7] relative grain-overlay">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Founder Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="flex flex-col md:flex-row items-center gap-8 md:gap-14 mb-20"
        >
          <div className="w-52 h-64 md:w-60 md:h-72 rounded-3xl overflow-hidden border-4 border-[#C0603A]/20 shadow-[0_8px_32px_rgba(192,96,58,0.15)] flex-shrink-0">
            <img
              src={LARONDA_IMG}
              alt="LaRonda Shree Henderson"
              className="w-full h-full object-cover object-[center_30%]"
              style={{ objectPosition: "center 25%" }}
              data-testid="founder-photo"
            />
          </div>
          <div className="text-center md:text-left">
            <p className="font-body text-xs font-bold tracking-[0.2em] uppercase text-[#C0603A] mb-2">
              Meet Our Founder
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight text-[#2C2C2A] font-medium mb-2">
              LaRonda Shree Henderson
            </h2>
            <p className="font-body text-sm font-semibold text-[#C0603A] mb-4">
              Founder / President / Owner
            </p>
            <p className="font-body text-base leading-relaxed text-[#3A3A38] max-w-lg">
              Driven by the loss of her beloved Oreo, LaRonda turned her deepest heartache into 
              a mission to save other fur families. Through 333Oreo Foundation and LaRonda Shree 
              Enterprises LLC, she is changing lives &mdash; one paw at a time.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center">
          {/* Left - Story */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
            className="md:col-span-7"
          >
            <p className="font-body text-xs font-bold tracking-[0.2em] uppercase text-[#C0603A] mb-4">
              Our Story
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight text-[#2C2C2A] font-medium mb-8">
              In Remembrance of Oreo Henderson
            </h2>
            <div className="space-y-5 font-body text-base leading-relaxed text-[#3A3A38]">
              <p>
                Oreo Henderson was more than a pet &mdash; he was family. When Oreo was hit by a car
                and broke his back, LaRonda Shree faced the most devastating moment of her life. Unable
                to produce the money for surgery and treatment, she was forced to make the heartbreaking
                decision to send Oreo to doggy heaven.
              </p>
              <p>
                That day changed everything. LaRonda established the <strong>333Oreo Foundation</strong> through
                LaRonda Shree Enterprises LLC to ensure that no fur parent ever has to endure the same
                heartache. Oreo's sacrifice was not in vain &mdash; it ignited a movement.
              </p>
              <p>
                <em>"Animal Lives Matter Too"</em> &mdash; as fur parents, we believe our fur babies
                are our babies, part of the family. Veterinary hospitals and emergency rooms should
                generate the same effort in saving their lives. We are on a mission to change these
                policies and go global.
              </p>
            </div>
          </motion.div>

          {/* Right - Highlight Card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="md:col-span-5"
          >
            <div className="bg-[#F2EFEB] border border-[#E8E3DD] rounded-3xl p-8 md:p-10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#C0603A]/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <Heart className="w-10 h-10 text-[#C0603A] mb-6" />
              <h3 className="font-heading text-2xl sm:text-3xl text-[#2C2C2A] mb-4">
                Our Mission
              </h3>
              <p className="font-body text-base leading-relaxed text-[#3A3A38] mb-6">
                To save fur parents from heartbreak by providing food, supplies, grooming,
                and advocating for affordable emergency veterinary care for all pets.
              </p>
              <div className="space-y-3">
                {[
                  "Pet Pantry for families in need",
                  "Affordable veterinary resources",
                  "Volunteer grooming services",
                  "Shelter food & medical supplies",
                  "Policy change advocacy",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Star className="w-4 h-4 text-[#C0603A] mt-0.5 flex-shrink-0" />
                    <span className="font-body text-sm text-[#3A3A38]">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
